import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getPost, trackView, absoluteUrl } from "@/lib/blog";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar, Clock, ChevronLeft, ChevronRight, Home, ArrowRight, Twitter, Linkedin, Facebook, Link2, Share2,
  MessageSquare, ChevronDown, Loader2, BookOpen, User,
} from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return ""; }
};

const Breadcrumb = ({ title }) => (
  <nav className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="blog-breadcrumb" aria-label="Breadcrumb">
    <Link to="/" className="hover:text-slate-900 inline-flex items-center gap-1"><Home className="h-3 w-3" />Home</Link>
    <ChevronRight className="h-3 w-3" />
    <Link to="/blog" className="hover:text-slate-900">Blog</Link>
    <ChevronRight className="h-3 w-3" />
    <span className="text-slate-700 truncate max-w-[40ch]">{title}</span>
  </nav>
);

const StickyToc = ({ items, activeId }) => {
  if (!items?.length) return null;
  return (
    <aside className="hidden lg:block" data-testid="blog-toc">
      <div className="sticky top-28 max-h-[70vh] overflow-y-auto pr-2">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">On this page</p>
        <ul className="space-y-1">
          {items.map((t) => (
            <li key={t.id} className={t.level === 3 ? "pl-3" : ""}>
              <a
                href={`#${t.id}`}
                className={`block text-[13px] leading-tight py-1.5 px-2 rounded transition-colors ${activeId === t.id ? "text-emerald-700 bg-emerald-50 font-semibold" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`}
              >
                {t.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
};

const ShareRow = ({ url, title }) => {
  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const copyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success("Link copied");
    }
  };
  return (
    <div className="flex items-center gap-2" data-testid="blog-share">
      <a href={tw} target="_blank" rel="noreferrer" aria-label="Share on Twitter" className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors"><Twitter className="h-4 w-4" /></a>
      <a href={li} target="_blank" rel="noreferrer" aria-label="Share on LinkedIn" className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors"><Linkedin className="h-4 w-4" /></a>
      <a href={fb} target="_blank" rel="noreferrer" aria-label="Share on Facebook" className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors"><Facebook className="h-4 w-4" /></a>
      <button type="button" onClick={copyLink} aria-label="Copy link" data-testid="blog-share-copy" className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors"><Link2 className="h-4 w-4" /></button>
    </div>
  );
};

const AuthorCard = ({ author }) => {
  if (!author) return null;
  const avatarUrl = author.avatar_url ? absoluteUrl(author.avatar_url) : null;
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 sm:p-6 flex gap-4 items-start" data-testid="blog-author-card">
      {avatarUrl ? (
        <img src={avatarUrl} alt={author.name} className="h-14 w-14 rounded-full object-cover ring-2 ring-white shadow-sm flex-shrink-0" />
      ) : (
        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 text-white text-lg font-bold flex items-center justify-center flex-shrink-0">
          {(author.name || "W").slice(0, 1)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-slate-900 text-base">{author.name}</span>
          {author.role && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{author.role}</span>}
        </div>
        {author.bio && <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{author.bio}</p>}
        {author.url && (
          <a href={author.url} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-800 font-medium transition-colors">
            <Link2 className="h-3.5 w-3.5" />
            {author.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </a>
        )}
        {(author.twitter || author.linkedin) && (
          <div className="mt-3 flex items-center gap-2">
            {author.twitter && (
              <a href={`https://twitter.com/${author.twitter.replace("@", "")}`} target="_blank" rel="noopener noreferrer" aria-label={`Follow ${author.name} on Twitter`} className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors">
                <Twitter className="h-3.5 w-3.5" />
              </a>
            )}
            {author.linkedin && (
              <a href={author.linkedin.startsWith("http") ? author.linkedin : `https://linkedin.com/in/${author.linkedin}`} target="_blank" rel="noopener noreferrer" aria-label={`Connect with ${author.name} on LinkedIn`} className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors">
                <Linkedin className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const NewsletterCta = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await subscribeNewsletter(email, "blog-post");
      setDone(true);
      toast.success("Subscribed! Check your inbox for confirmation.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="rounded-3xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50 via-white to-blue-50 p-6 sm:p-8" data-testid="blog-newsletter-cta">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">Newsletter</div>
      <h3 className="mt-1.5 text-xl sm:text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>The Wappit Weekly</h3>
      <p className="mt-1 text-sm text-slate-600">One short email a week with WhatsApp Business API tips, template ideas and product updates. Zero spam.</p>
      {done ? (
        <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700" data-testid="blog-newsletter-success">
          ✓ You&apos;re in. See you in your inbox.
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 flex gap-2">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="rounded-full h-11" data-testid="blog-newsletter-email" />
          <Button type="submit" disabled={loading} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 px-5 font-semibold" data-testid="blog-newsletter-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
          </Button>
        </form>
      )}
    </div>
  );
};

const FaqBlock = ({ items }) => {
  const [open, setOpen] = useState(0);
  if (!items?.length) return null;
  return (
    <section className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="blog-faq">
      <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Frequently asked questions</h3>
      <ul className="mt-4 divide-y divide-slate-100">
        {items.map((q, i) => (
          <li key={i} className="py-2" data-testid={`blog-faq-item-${i}`}>
            <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 py-2 text-left">
              <span className="font-semibold text-slate-900">{q.question}</span>
              <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && (
              <div className="pt-1 pb-3 text-[14.5px] text-slate-700 leading-relaxed prose-blog" dangerouslySetInnerHTML={{ __html: q.answer || "" }} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

const PrevNext = ({ prev, next }) => {
  if (!prev && !next) return null;
  return (
    <nav className="mt-10 grid sm:grid-cols-2 gap-4" aria-label="Article navigation" data-testid="blog-prev-next">
      {prev ? (
        <Link to={`/blog/${prev.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-emerald-300 hover:shadow-md transition-all">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1.5"><ChevronLeft className="h-3 w-3" />Previous</div>
          <div className="mt-1 font-bold text-slate-900 group-hover:text-emerald-700 line-clamp-2">{prev.title}</div>
        </Link>
      ) : <div />}
      {next ? (
        <Link to={`/blog/${next.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-emerald-300 hover:shadow-md transition-all text-right">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center justify-end gap-1.5">Next<ChevronRight className="h-3 w-3" /></div>
          <div className="mt-1 font-bold text-slate-900 group-hover:text-emerald-700 line-clamp-2">{next.title}</div>
        </Link>
      ) : <div />}
    </nav>
  );
};

export default function BlogDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState("");
  const articleRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setPost(null);
    getPost(slug)
      .then((p) => { if (!cancel) setPost(p); trackView(slug); })
      .catch(() => { if (!cancel) nav("/blog", { replace: true }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [slug]);

  // TOC scroll spy
  useEffect(() => {
    if (!post?.toc?.length) return;
    let raf = 0;
    const tick = () => {
      raf = 0;
      let current = post.toc[0]?.id || "";
      for (const t of post.toc) {
        const el = document.getElementById(t.id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - 120 <= 0) current = t.id;
      }
      setActiveId(current);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(tick); };
    window.addEventListener("scroll", onScroll, { passive: true });
    tick();
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [post?.toc]);

  // Imperatively override head meta tags so a previous SeoHead mutation (from Landing)
  // doesn't leave stale og:title / description / canonical when SPA-navigating to a blog post.
  useEffect(() => {
    if (!post) return;
    const seoData = post.seo || {};
    const ogImg = (typeof window !== "undefined" && post.cover_image?.url && !/^https?:/i.test(post.cover_image.url))
      ? `${process.env.REACT_APP_BACKEND_URL || ""}${post.cover_image.url}`
      : (seoData.og_image || post.cover_image?.url || "");
    const url = typeof window !== "undefined" ? `${window.location.origin}/blog/${post.slug}` : `/blog/${post.slug}`;
    const can = seoData.canonical_url || url;
    const title = `${seoData.title || post.title} — Wappit Blog`;
    const desc = seoData.description || post.excerpt || "";
    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    document.title = title;
    setMeta("description", desc);
    if (seoData.focus_keyword) setMeta("keywords", seoData.focus_keyword);
    setMeta("robots", seoData.robots_index === false ? "noindex,nofollow" : "index,follow");
    setMeta("og:type", "article", "property");
    setMeta("og:title", seoData.og_title || seoData.title || post.title, "property");
    setMeta("og:description", seoData.og_description || desc, "property");
    setMeta("og:url", can, "property");
    if (ogImg) setMeta("og:image", ogImg, "property");
    setMeta("twitter:card", seoData.twitter_card || "summary_large_image");
    setMeta("twitter:title", seoData.twitter_title || seoData.title || post.title);
    setMeta("twitter:description", seoData.twitter_description || desc);
    if (ogImg) setMeta("twitter:image", ogImg);
    setLink("canonical", can);
  }, [post]);

  if (loading) {
    return (
      <>
        <Navbar onBookDemo={() => {}} />
        <main className="min-h-screen flex items-center justify-center bg-slate-50">
          <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
        </main>
      </>
    );
  }
  if (!post) return null;

  const seo = post.seo || {};
  const cover = absoluteUrl(post.cover_image?.url);
  const og = absoluteUrl(seo.og_image || post.cover_image?.url || "");
  const pageUrl = typeof window !== "undefined" ? `${window.location.origin}/blog/${post.slug}` : `/blog/${post.slug}`;
  const canonical = seo.canonical_url || pageUrl;

  return (
    <>
      <Helmet>
        <title>{`${seo.title || post.title} — Wappit Blog`}</title>
        <meta name="description" content={seo.description || post.excerpt || ""} />
        {seo.focus_keyword && <meta name="keywords" content={seo.focus_keyword} />}
        <link rel="canonical" href={canonical} />
        <meta name="robots" content={seo.robots_index === false ? "noindex,nofollow" : "index,follow"} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seo.og_title || seo.title || post.title} />
        <meta property="og:description" content={seo.og_description || seo.description || post.excerpt || ""} />
        <meta property="og:url" content={canonical} />
        {og && <meta property="og:image" content={og} />}
        <meta name="twitter:card" content={seo.twitter_card || "summary_large_image"} />
        <meta name="twitter:title" content={seo.twitter_title || seo.title || post.title} />
        <meta name="twitter:description" content={seo.twitter_description || seo.description || post.excerpt || ""} />
        {og && <meta name="twitter:image" content={og} />}
        {(post.jsonld || []).map((j, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(j)}</script>
        ))}
      </Helmet>

      <Navbar onBookDemo={() => {}} />

      <main className="bg-white min-h-screen" data-testid="blog-detail-page">
        {/* Header */}
        <header className="relative overflow-hidden pt-28 sm:pt-32 pb-10">
          <div className="absolute -top-32 -right-20 h-[420px] w-[520px] rounded-full bg-emerald-100/40 blur-3xl pointer-events-none" />
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
            <Breadcrumb title={post.title} />
            {post.category && (
              <Link to={`/blog?category=${post.category.slug}`} className="mt-5 inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-[0.18em] text-emerald-700 hover:text-emerald-900">
                {post.category.name}
              </Link>
            )}
            <h1 data-testid="blog-detail-title" className="mt-3 text-4xl sm:text-5xl lg:text-[3.25rem] font-extrabold text-slate-900 tracking-tight leading-[1.1]" style={{ fontFamily: "Outfit, sans-serif" }}>
              {post.title}
            </h1>
            {post.excerpt && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>}

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-slate-600">
              {post.author && (
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 text-white text-[11px] font-bold flex items-center justify-center">
                    {(post.author.name || "W").slice(0, 1)}
                  </div>
                  <span className="font-semibold text-slate-800">{post.author.name}</span>
                </div>
              )}
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{fmtDate(post.published_at)}</span>
              {post.updated_at && post.updated_at !== post.published_at && (
                <span className="inline-flex items-center gap-1.5 text-slate-500">Updated {fmtDate(post.updated_at)}</span>
              )}
              <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{post.reading_time_minutes || 1} min read</span>
              <span className="inline-flex items-center gap-1.5 text-slate-500"><BookOpen className="h-3.5 w-3.5" />{post.views || 0} views</span>
            </div>
          </div>
        </header>

        {/* Cover */}
        {cover && (
          <figure className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="relative aspect-[16/8] rounded-3xl overflow-hidden shadow-xl ring-1 ring-slate-200">
              <img src={cover} alt={post.cover_image?.alt || post.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            </div>
            {post.cover_image?.caption && <figcaption className="text-center mt-2 text-xs text-slate-500">{post.cover_image.caption}</figcaption>}
          </figure>
        )}

        {/* Body + TOC */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-10 grid lg:grid-cols-[1fr_280px] gap-10">
          <article ref={articleRef} className="min-w-0">
            <div
              className="prose-blog"
              data-testid="blog-detail-content"
              dangerouslySetInnerHTML={{ __html: post.content_html || "" }}
            />

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2" data-testid="blog-tags">
                {post.tags.map((t) => (
                  <Link key={t.id} to={`/blog?tag=${t.slug}`} className="inline-flex items-center px-3 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-slate-700">
                    #{t.name}
                  </Link>
                ))}
              </div>
            )}

            {/* CTA banner */}
            {post.cta?.enabled && (
              <aside className="mt-8 rounded-3xl bg-gradient-to-br from-slate-900 to-blue-950 text-white p-7 sm:p-9 ring-1 ring-slate-800 shadow-[0_20px_50px_-20px_rgba(2,6,23,0.5)]" data-testid="blog-cta">
                <h3 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{post.cta.title}</h3>
                <p className="mt-2 text-slate-300">{post.cta.description}</p>
                <a href={post.cta.href} target={post.cta.href?.startsWith("http") ? "_blank" : undefined} rel={post.cta.href?.startsWith("http") ? "noopener noreferrer" : undefined} className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-5 h-11">
                  {post.cta.label} <ArrowRight className="h-4 w-4" />
                </a>
              </aside>
            )}

            {/* Share */}
            <div className="mt-8 flex items-center gap-4 border-t border-b border-slate-100 py-4">
              <span className="text-xs uppercase font-bold tracking-[0.18em] text-slate-500 flex items-center gap-1.5"><Share2 className="h-3 w-3" /> Share</span>
              <ShareRow url={pageUrl} title={post.title} />
            </div>

            {/* Author Card */}
            <AuthorCard author={post.author} />

            {/* FAQ */}
            <FaqBlock items={post.faqs} />

            {/* Newsletter */}
            <div className="mt-10"><NewsletterCta /></div>

            {/* Prev/Next */}
            <PrevNext prev={post.prev} next={post.next} />

            {/* Comments placeholder */}
            <section className="mt-10 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500" data-testid="blog-comments-placeholder">
              <MessageSquare className="h-5 w-5 mx-auto text-slate-400" />
              <div className="mt-2 font-semibold text-slate-700">Comments are coming soon.</div>
              <div className="mt-1">Reply on social or email us — we read everything.</div>
            </section>
          </article>

          <StickyToc items={post.toc} activeId={activeId} />
        </section>

        {/* Related */}
        {post.related?.length > 0 && (
          <section className="bg-slate-50 border-t border-slate-200 py-12">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
              <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Related articles</h3>
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-related">
                {post.related.map((r) => (
                  <Link key={r.id} to={`/blog/${r.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-xl hover:-translate-y-0.5 transition-all">
                    {r.category && <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">{r.category.name}</div>}
                    <h4 className="mt-1.5 font-bold text-slate-900 group-hover:text-emerald-700 line-clamp-2 leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>{r.title}</h4>
                    <div className="mt-2 text-xs text-slate-500 flex items-center gap-3"><Calendar className="h-3 w-3" />{fmtDate(r.published_at)}<Clock className="h-3 w-3" />{r.reading_time_minutes || 1} min</div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}
